import React, { useEffect, useMemo } from "react";

import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { useGetDatesThatMatterFilterOptions } from "@/utils/react-query-hooks/lenses";
import { SignalCategoriesProvider } from "@/components/analysisModules/TopicClusteringTreeView/SignalCategoriesContext";
import { buildSignalCategoryTheme } from "@/components/analysisModules/comps/SignalFilters/signalCategories";
import {
  SignalCategoryTrendChart,
  useSignalCategoryTrendData,
} from "@/components/analysisModules/TopicClusteringTreeViewV2/SignalCategoryTrend";

import {
  WidgetEmptyState,
  WidgetLoadingState,
} from "../../../../WidgetRenderer/comps/altStatePlaceholders";
import { useUpdateLoadedWidgetsTracker } from "../../../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { useReportLandingContext } from "../../../../../ReportLandingContext";

import { DEFAULTS, resolvePeriod } from "../TempPriorities/settings";
import { SignalCategoryTrendConfigForm } from "./SignalCategoryTrendConfigForm";

/**
 * TempSignalCategoryTrend — the Overview dashboard's "Signal categories over
 * time" as a report widget: one line per signal category, observations
 * carrying a signal of that category per period, over the history window
 * before the picked period. The dashboard's own chart and legend render as
 * is (SignalCategoryTrendChart); the widget card replaces the dashboard's
 * shell.
 *
 * Period: the same picker as the Priorities widget ("Latest month" keeps
 * moving forward), and the same fallback when a saved period leaves the
 * options; "Trend over" (2 / 3 / 6 periods, as on Priorities) sets how many
 * periods the chart spans, ending on it. Scope: the report's filters plus
 * the widget's own.
 *
 * Categories: the tenant's, from platformConfigs.SIGNAL_CATEGORIES (the PM
 * four when unset) — the dashboard gets the same list off the scorer's
 * payload, which this widget doesn't request.
 *
 * Public-report snapshot: the period, the per-period counts and the
 * categories they were drawn with are pushed to the report's capture store,
 * and `prefetchedData` replays them — no request is made, and the line
 * names, order and colours stay what they were when the link was shared.
 * A snapshot saved before the categories were stored falls back to the
 * live platform configs.
 *
 * Not carried over: the dashboard's single-period lead-in and blur note
 * (they read the scorer's meta).
 */

// Same gate as the Priorities widget: signals + the dates-that-matter lens
// are split-mode only.
export const optionDef = {
  display: "Signal categories over time",
  icon: "Activity", // same as the Line Chart widget; oolib has no ChartLine
  desc: "One line per signal category, over time",
  isAvailableToAdd: ({ platformConfigs }) => getAnnoMode(platformConfigs) === "split",
};

export const Component = ({
  periodValue = DEFAULTS.periodValue,
  trendWindow = DEFAULTS.trendWindow,
  widgetFilters = [],
  activeFilters = [],
  widgetIdx = 0,
  id,
  prefetchedData,
}) => {
  const { updateWidgetDataStore } = useReportLandingContext() || {};
  const platformConfigs = useGetQueryData("platformConfigs");
  // A snapshot's own categories win, even when it saved none (null = the PM
  // defaults then); only a snapshot from before they were stored reads live.
  const signalCategories =
    prefetchedData && "signalCategories" in prefetchedData
      ? prefetchedData.signalCategories
      : platformConfigs?.SIGNAL_CATEGORIES;
  const categoryTheme = useMemo(() => buildSignalCategoryTheme(signalCategories), [signalCategories]);

  const { data: dateFilterOptions, isLoading: periodsFetching } =
    useGetDatesThatMatterFilterOptions({ enabled: !prefetchedData });
  const periodsLoading = !prefetchedData && periodsFetching;
  const period = useMemo(
    () =>
      prefetchedData
        ? prefetchedData.period
        : resolvePeriod(dateFilterOptions?.periods, periodValue),
    [prefetchedData, dateFilterOptions, periodValue],
  );

  const filters = useMemo(() => [...activeFilters, ...widgetFilters], [activeFilters, widgetFilters]);

  return (
    <SignalCategoriesProvider value={categoryTheme}>
      <Trend
        period={period}
        trendWindow={trendWindow}
        periodsLoading={periodsLoading}
        dateFieldMap={dateFilterOptions?.dateFieldMap}
        activeFilters={filters}
        prefetchedData={prefetchedData}
        signalCategories={signalCategories}
        widgetIdx={widgetIdx}
        id={id}
        updateWidgetDataStore={updateWidgetDataStore}
      />
    </SignalCategoriesProvider>
  );
};

// Inside the provider, so the data hook reads the tenant's categories.
const Trend = ({ period, trendWindow, periodsLoading, dateFieldMap, activeFilters, prefetchedData, signalCategories, widgetIdx, id, updateWidgetDataStore }) => {
  const trend = useSignalCategoryTrendData({
    period,
    dateFieldMap,
    activeFilters,
    prefetchedRows: prefetchedData?.rowsByPeriod,
    // "Trend over N periods" = the period plus the N - 1 before it
    historyPeriods: Math.max(0, trendWindow - 1),
    // the author picked the window: an empty period in it still shows
    trimLeadingEmpty: false,
  });

  const loading = periodsLoading || (!!period && trend.isLoading);
  useUpdateLoadedWidgetsTracker({
    status: loading ? "loading" : "success",
    widgetIdx,
  });

  // Capture for the public snapshot once the counts are in.
  useEffect(() => {
    if (prefetchedData || !updateWidgetDataStore || !id || !trend.rowsByPeriod) return;
    updateWidgetDataStore(id, { period, rowsByPeriod: trend.rowsByPeriod, signalCategories: signalCategories || null });
  }, [prefetchedData, updateWidgetDataStore, id, period, trend.rowsByPeriod, signalCategories]);

  if (periodsLoading) return <WidgetLoadingState />;
  if (!period)
    return <WidgetEmptyState display="No periods to show — no dated annotations yet" />;

  return (
    <div style={{ padding: "1.6rem" }}>
      <SignalCategoryTrendChart {...trend} />
    </div>
  );
};

export const ConfigForm = SignalCategoryTrendConfigForm;
